import { formatKes } from "lib/format";
import { useEffect, useState } from "react";
import Widget from "components/widget/Widget";
import Card from "components/card";
import StatusBadge from "components/admin/StatusBadge";
import { MdViewList, MdHourglassTop, MdCheckCircle, MdAttachMoney } from "react-icons/md";
import { apiGet } from "lib/apiClient";
import { Link } from "react-router-dom";
import HubsWidget from "./HubsWidget";
import { RevenueSnapshot } from "components/revenue/RevenueStatement";

const COMPLIANCE_TONE = { APPROVED: "green", PENDING: "amber", SUSPENDED: "red" };
const SETTLEMENT_STATUS_TONE = {
  CALCULATED: "gray",
  PENDING_APPROVAL: "amber",
  APPROVED: "blue",
  PROCESSING: "blue",
  PAID: "green",
  FAILED: "red",
  REVERSED: "red",
};

export default function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = () =>
      apiGet("/portal/dashboard")
        .then(setSummary)
        .catch((err) => setError(err.message || "Failed to load dashboard."));
    load();
    // Revenue and settlement figures here move the instant a deposit/settlement is
    // confirmed server-side; without this a tab left open just shows a stale snapshot.
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  return (
    <div>
      {error && <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error}</div>}

      {summary && (
        <div className="mb-5 flex items-center justify-between">
          <div>
            <p className="text-2xl font-semibold text-navy-700 dark:text-white">{summary.legalName}</p>
          </div>
          <StatusBadge label={summary.complianceStatus} tone={COMPLIANCE_TONE[summary.complianceStatus] || "gray"} />
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <Widget icon={<MdViewList className="h-7 w-7" />} title="Assigned markets" subtitle={summary ? summary.marketsCount : "…"} />
        <Widget icon={<MdHourglassTop className="h-7 w-7" />} title="Pending settlements" subtitle={summary ? summary.pendingSettlementsCount : "…"} />
        <Widget icon={<MdCheckCircle className="h-7 w-7" />} title="Paid settlements" subtitle={summary ? summary.paidSettlementsCount : "…"} />
        <Widget icon={<MdAttachMoney className="h-7 w-7" />} title="Lifetime paid out" subtitle={summary ? formatKes(summary.lifetimeClientShareAmount) : "…"} />
      </div>

      <div className="mt-5">
        <RevenueSnapshot basePath="/portal/revenue" href="/portal/revenue" LinkComponent={Link} />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <HubsWidget />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <Card extra="p-5 xl:col-span-2">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Latest settlement</p>
          {summary && summary.latestSettlement ? (
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">Period</span>
                <span className="font-semibold text-navy-700 dark:text-white">
                  {new Date(summary.latestSettlement.periodStart).toLocaleDateString()} – {new Date(summary.latestSettlement.periodEnd).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">Net revenue</span>
                <span className="font-semibold text-navy-700 dark:text-white">{formatKes(summary.latestSettlement.netRevenue, summary.latestSettlement.currency)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">Your share</span>
                <span className="font-semibold text-navy-700 dark:text-white">
                  {formatKes(summary.latestSettlement.clientShareAmount, summary.latestSettlement.currency)} ({summary.latestSettlement.clientSharePercentage}%)
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-300">Status</span>
                <StatusBadge label={summary.latestSettlement.status} tone={SETTLEMENT_STATUS_TONE[summary.latestSettlement.status] || "gray"} />
              </div>
            </div>
          ) : (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">No settlements have been calculated yet.</p>
          )}
          <Link to="/portal/settlements" className="mt-4 inline-block text-sm font-semibold text-brand-500">
            View all settlements →
          </Link>
        </Card>
        <Card extra="p-5">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Your markets</p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {summary ? summary.marketsCount : "…"} market(s) currently assigned to your organization.
          </p>
          <Link to="/portal/markets" className="mt-3 inline-block text-sm font-semibold text-brand-500">
            View markets →
          </Link>
        </Card>
      </div>
    </div>
  );
}
